import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import AddIcon from '@mui/icons-material/Add';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { IUser } from '../../types/damTypes';
import { Issue, Task } from './types';
import { fetchIssues, fetchTasks, reorderIssues, reorderTasks, createTask, updateTask, deleteTask, deleteIssue } from './api';
import IssuesListView from './IssuesListView';
import IssuesKanbanView from './IssuesKanbanView';
import TasksBoard from './TasksBoard';
import TasksListView from './TasksListView';
import IssueFormDialog from './IssueFormDialog';
import TaskFormDialog from './TaskFormDialog';
import { hasPermission } from '../../utils/permissionUtils';

type ViewMode = 'list' | 'kanban';

interface IssuesTasksPageProps {
  currentUser: IUser | null;
}

const IssuesTasksPage: React.FC<IssuesTasksPageProps> = ({ currentUser }) => {
  const [tab, setTab] = useState<'issues' | 'tasks'>('issues');
  const [issuesView, setIssuesView] = useState<ViewMode>('kanban');
  const [tasksView, setTasksView] = useState<ViewMode>('kanban');
  const [issues, setIssues] = useState<Issue[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [issuesError, setIssuesError] = useState<string | null>(null);
  const [tasksError, setTasksError] = useState<string | null>(null);

  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [editingIssue, setEditingIssue] = useState<Issue | null>(null);
  const [issueDraft, setIssueDraft] = useState<Partial<Issue> | null>(null);

  // Task dialog state for list view editing
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  const canReadIssues = hasPermission(currentUser, 'issues:read');
  const canCreateIssues = hasPermission(currentUser, 'issues:create');
  const canUpdateIssues = hasPermission(currentUser, 'issues:update');
  const canDeleteIssues = hasPermission(currentUser, 'issues:delete');
  const canReadTasks = hasPermission(currentUser, 'tasks:read');
  const canCreateTasks = hasPermission(currentUser, 'tasks:create');
  const canUpdateTasks = hasPermission(currentUser, 'tasks:update');
  const canDeleteTasks = hasPermission(currentUser, 'tasks:delete');

  const loadIssues = useCallback(async () => {
    setIssuesLoading(true);
    setIssuesError(null);
    try {
      const data = await fetchIssues();
      setIssues(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setIssuesError(err?.message || 'Kunde inte hämta issues');
    } finally {
      setIssuesLoading(false);
    }
  }, []);

  const loadTasks = useCallback(async () => {
    setTasksLoading(true);
    setTasksError(null);
    try {
      const data = await fetchTasks();
      setTasks(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setTasksError(err?.message || 'Kunde inte hämta tasks');
    } finally {
      setTasksLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canReadIssues) {
      loadIssues();
    } else {
      setIssues([]);
    }
    if (canReadTasks) {
      loadTasks();
    } else {
      setTasks([]);
    }
  }, [loadIssues, loadTasks, canReadIssues, canReadTasks]);

  useEffect(() => {
    const onReportBug = (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      setTab('issues');
      setEditingIssue(null);
      setIssueDraft({
        type: 'bug',
        status: 'new',
        priority: 2,
        category: 'Other',
        contextUrl: detail.contextUrl || window.location.href
      } as Partial<Issue>);
      setIssueDialogOpen(true);
    };
    window.addEventListener('issuesTasks:reportBug', onReportBug as EventListener);
    return () => window.removeEventListener('issuesTasks:reportBug', onReportBug as EventListener);
  }, []);

  const handleOpenNewIssue = (prefill?: Partial<Issue>) => {
    setEditingIssue(null);
    setIssueDraft(prefill || null);
    setIssueDialogOpen(true);
  };

  const handleEditIssue = (issue: Issue) => {
    setEditingIssue(issue);
    setIssueDraft(null);
    setIssueDialogOpen(true);
  };

  const handleIssueSaved = async () => {
    setIssueDialogOpen(false);
    await loadIssues();
  };

  const handleIssueDelete = async (issueId: string) => {
    if (!window.confirm('Är du säker på att du vill ta bort denna issue?')) return;
    try {
      await deleteIssue(issueId);
      await loadIssues();
    } catch (err) {
      setIssuesError('Kunde inte ta bort issue');
    }
  };

  const handleIssuesReorder = async (nextIssues: Issue[], items: Array<{ id: string; status: string; boardOrder: number }>) => {
    setIssues(nextIssues);
    try {
      await reorderIssues(items);
    } catch {
      loadIssues();
    }
  };

  const handleTaskCreate = async (payload: Record<string, any>) => {
    await createTask(payload);
    await loadTasks();
  };

  const handleTaskUpdate = async (taskId: string, payload: Record<string, any>) => {
    await updateTask(taskId, payload);
    await loadTasks();
  };

  const handleTaskDelete = async (taskId: string) => {
    await deleteTask(taskId);
    await loadTasks();
  };

  const handleTasksReorder = async (nextTasks: Task[], items: Array<{ id: string; status: string; boardOrder: number }>) => {
    setTasks(nextTasks);
    try {
      await reorderTasks(items);
    } catch {
      loadTasks();
    }
  };

  // Task list view handlers
  const handleEditTaskFromList = (task: Task) => {
    setEditingTask(task);
    setTaskDialogOpen(true);
  };

  const handleDeleteTaskFromList = async (taskId: string) => {
    if (!window.confirm('Är du säker på att du vill ta bort denna uppgift?')) return;
    try {
      await deleteTask(taskId);
      await loadTasks();
    } catch (err) {
      setTasksError('Kunde inte ta bort uppgift');
    }
  };

  useEffect(() => {
    if (tab === 'issues' && !canReadIssues && canReadTasks) setTab('tasks');
    if (tab === 'tasks' && !canReadTasks && canReadIssues) setTab('issues');
  }, [tab, canReadIssues, canReadTasks]);

  // Count helpers for badges
  const issuesCount = issues.length;
  const openIssuesCount = issues.filter(i => i.status !== 'done').length;
  const tasksCount = tasks.length;
  const openTasksCount = tasks.filter(t => t.status !== 'done').length;

  // View toggle component
  const ViewToggle = ({ value, onChange }: { value: ViewMode; onChange: (v: ViewMode) => void }) => (
    <ToggleButtonGroup
      size="small"
      value={value}
      exclusive
      onChange={(_, v) => v && onChange(v)}
      sx={{ 
        backgroundColor: 'background.paper',
        '& .MuiToggleButton-root': {
          px: 1.5,
          py: 0.5,
        }
      }}
    >
      <ToggleButton value="list" title="Listvy">
        <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} />
        <Typography variant="caption">Lista</Typography>
      </ToggleButton>
      <ToggleButton value="kanban" title="Kanban-vy">
        <ViewKanbanIcon fontSize="small" sx={{ mr: 0.5 }} />
        <Typography variant="caption">Kanban</Typography>
      </ToggleButton>
    </ToggleButtonGroup>
  );

  const issueActions = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      <ViewToggle value={issuesView} onChange={setIssuesView} />
      {canCreateIssues && (
        <>
          <Button 
            variant="outlined" 
            startIcon={<BugReportIcon />} 
            onClick={() => handleOpenNewIssue({ type: 'bug', status: 'new', priority: 2 })}
            color="error"
            size="small"
          >
            Rapportera bugg
          </Button>
          <Button 
            variant="contained" 
            startIcon={<AddIcon />} 
            onClick={() => handleOpenNewIssue()}
            size="small"
          >
            Nytt ärende
          </Button>
        </>
      )}
    </Box>
  );

  const taskActions = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
      <ViewToggle value={tasksView} onChange={setTasksView} />
      {canCreateTasks && (
        <Button 
          variant="contained" 
          startIcon={<AddIcon />} 
          onClick={() => {
            setEditingTask(null);
            setTaskDialogOpen(true);
          }}
          size="small"
        >
          Ny uppgift
        </Button>
      )}
    </Box>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
            Buggar & Roadmap
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Hantera buggar, förbättringar och arbetsuppgifter
          </Typography>
        </Box>
        {tab === 'issues' && canReadIssues && issueActions}
        {tab === 'tasks' && canReadTasks && taskActions}
      </Box>

      {/* Tabs with better visual hierarchy */}
      <Paper sx={{ mb: 3, overflow: 'hidden' }}>
        <Tabs 
          value={tab} 
          onChange={(_, value) => setTab(value)} 
          indicatorColor="primary" 
          textColor="primary"
          sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': {
              minHeight: 56,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '0.95rem',
            }
          }}
        >
          {canReadIssues && (
            <Tab 
              value="issues" 
              icon={<AssignmentIcon />}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Ärenden</span>
                  {issuesCount > 0 && (
                    <Chip 
                      size="small" 
                      label={openIssuesCount > 0 ? `${openIssuesCount} öppna` : 'Alla klara'}
                      color={openIssuesCount > 0 ? 'primary' : 'success'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              }
            />
          )}
          {canReadTasks && (
            <Tab 
              value="tasks" 
              icon={<TaskAltIcon />}
              iconPosition="start"
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>Uppgifter / Go-Live</span>
                  {tasksCount > 0 && (
                    <Chip 
                      size="small" 
                      label={openTasksCount > 0 ? `${openTasksCount} kvar` : 'Alla klara'}
                      color={openTasksCount > 0 ? 'warning' : 'success'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              }
            />
          )}
        </Tabs>
      </Paper>

      {tab === 'issues' && canReadIssues && (
        <Box>
          {issuesError && <Alert severity="error" sx={{ mb: 2 }}>{issuesError}</Alert>}
          {issuesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {issuesView === 'list' ? (
                <IssuesListView
                  issues={issues}
                  onEditIssue={handleEditIssue}
                  onDeleteIssue={handleIssueDelete}
                  canEdit={canUpdateIssues}
                  canDelete={canDeleteIssues}
                />
              ) : (
                <IssuesKanbanView
                  issues={issues}
                  onEditIssue={handleEditIssue}
                  onReorder={handleIssuesReorder}
                  canDrag={canUpdateIssues}
                  canEdit={canUpdateIssues}
                />
              )}
            </>
          )}
        </Box>
      )}

      {tab === 'tasks' && canReadTasks && (
        <Box>
          {tasksError && <Alert severity="error" sx={{ mb: 2 }}>{tasksError}</Alert>}
          {tasksLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {tasksView === 'list' ? (
                <TasksListView
                  tasks={tasks}
                  onEditTask={handleEditTaskFromList}
                  onDeleteTask={handleDeleteTaskFromList}
                  canEdit={canUpdateTasks}
                  canDelete={canDeleteTasks}
                />
              ) : (
                <TasksBoard
                  tasks={tasks}
                  onCreateTask={handleTaskCreate}
                  onUpdateTask={handleTaskUpdate}
                  onDeleteTask={handleTaskDelete}
                  onReorder={handleTasksReorder}
                  canCreate={canCreateTasks}
                  canUpdate={canUpdateTasks}
                  canDelete={canDeleteTasks}
                />
              )}
            </>
          )}
        </Box>
      )}

      <Divider sx={{ my: 3 }} />
      <Typography variant="caption" color="text.secondary">
        Endast admin och redaktör har åtkomst till denna modul.
      </Typography>

      <IssueFormDialog
        open={issueDialogOpen}
        issue={editingIssue}
        initialValues={issueDraft || undefined}
        currentUser={currentUser}
        onClose={() => setIssueDialogOpen(false)}
        onSaved={handleIssueSaved}
      />

      {/* Task Dialog for list view editing */}
      <TaskFormDialog
        open={taskDialogOpen}
        task={editingTask}
        onClose={() => {
          setTaskDialogOpen(false);
          setEditingTask(null);
        }}
        onSave={async (payload) => {
          if (editingTask) {
            await handleTaskUpdate(editingTask._id, payload);
          } else {
            await handleTaskCreate(payload);
          }
          setTaskDialogOpen(false);
          setEditingTask(null);
        }}
        onDelete={editingTask ? async () => {
          await handleTaskDelete(editingTask._id);
          setTaskDialogOpen(false);
          setEditingTask(null);
        } : undefined}
        canDelete={canDeleteTasks}
      />
    </Box>
  );
};

export default IssuesTasksPage;
